// Types
import { Matrix3D } from 'rematrix';
import type { CSSProperties } from 'react';
import { getScale } from '../../../common/maths/matrix/matrixUtils';

const INVERSE_ZOOM_SCALE_CSS_PROPERTY = '--inverse-zoom-scale';

// Drag layers can provide a hovered drop target's scale instead of the ambient canvas zoom. Inverse-scaled elements
// defer to that value without needing to re-render.
//
// It has to be a second property rather than a shadowed `--inverse-zoom-scale`, because a custom property that
//  references itself is a cycle - it computes to guaranteed-invalid and falls back rather than inheriting.
const PROVIDED_INVERSE_ZOOM_SCALE_CSS_PROPERTY = '--provided-inverse-zoom-scale';

// Inverse-scaled elements opt in with this attribute.
export const INVERSE_ZOOM_SCALE_ATTRIBUTE = 'data-inverse-zoom-scale';
const SCOPED_INVERSE_ZOOM_SCALE_SELECTORS = `[${INVERSE_ZOOM_SCALE_ATTRIBUTE}]`;
const lastRenderedInverseZoomScales = new WeakMap<HTMLElement, string>();

const MAX_INVERSE_ZOOM_SCALE = 2;
const MIN_INVERSE_ZOOM_SCALE = 1;

/** Derives the clamped inverse scale used by every write path. */
export const getClampedInverseZoomScale = (scale?: number | null): string => {
    if (!scale || !Number.isFinite(scale)) return String(MIN_INVERSE_ZOOM_SCALE);

    return String(Math.min(Math.max(1 / scale, MIN_INVERSE_ZOOM_SCALE), MAX_INVERSE_ZOOM_SCALE));
};

/** Provides a counter-scale to a drag-layer subtree. */
export const getProvidedInverseZoomScaleStyle = (scale?: number | null): CSSProperties =>
    ({
        [PROVIDED_INVERSE_ZOOM_SCALE_CSS_PROPERTY]: getClampedInverseZoomScale(scale),
    }) as CSSProperties;

const getDeferredInverseZoomScale = (scale?: number | null): string =>
    `var(${PROVIDED_INVERSE_ZOOM_SCALE_CSS_PROPERTY}, ${getClampedInverseZoomScale(scale)})`;

export const getInverseZoomScaleStyle = (scale?: number | null): CSSProperties =>
    ({
        [INVERSE_ZOOM_SCALE_CSS_PROPERTY]: getDeferredInverseZoomScale(scale),
    }) as CSSProperties;

export const getDirectInverseZoomScaleStyle = (scale?: number | null): CSSProperties =>
    ({
        scale: getDeferredInverseZoomScale(scale),
    }) as CSSProperties;

// Direct scale writes avoid recalculating a node's whole subtree. The trade-off is that descendants cannot read the
// value from a CSS variable.
export const DIRECT_SCALE_ATTRIBUTE_VALUE = 'scale';

const applyInverseZoomScale = (domElement: HTMLElement, inverseZoomScale: string): void => {
    if (domElement.getAttribute(INVERSE_ZOOM_SCALE_ATTRIBUTE) === DIRECT_SCALE_ATTRIBUTE_VALUE) {
        if (domElement.style.getPropertyValue('scale') === inverseZoomScale) return;

        domElement.style.setProperty('scale', inverseZoomScale);
        return;
    }

    if (domElement.style.getPropertyValue(INVERSE_ZOOM_SCALE_CSS_PROPERTY) === inverseZoomScale) return;

    domElement.style.setProperty(INVERSE_ZOOM_SCALE_CSS_PROPERTY, inverseZoomScale);
};

const renderScopedInverseZoomScaleCSS = (domElement: HTMLElement, inverseZoomScale: string): void => {
    const inverseScaledElements = domElement.querySelectorAll<HTMLElement>(SCOPED_INVERSE_ZOOM_SCALE_SELECTORS);

    inverseScaledElements.forEach((inverseScaledElement) => {
        applyInverseZoomScale(inverseScaledElement, inverseZoomScale);
    });
};

/** Writes the current inverse scale to opted-in elements. */
export const renderInverseZoomScaleCSS = (domElement: HTMLElement | null, transformationMatrix: Matrix3D): void => {
    if (!domElement || !transformationMatrix) return;

    // Preserve the last valid value when the matrix has no usable scale.
    const scale = getScale(transformationMatrix);
    if (!scale) return;

    const inverseZoomScale = getClampedInverseZoomScale(scale);
    if (lastRenderedInverseZoomScales.get(domElement) === inverseZoomScale) return;

    renderScopedInverseZoomScaleCSS(domElement, inverseZoomScale);
    lastRenderedInverseZoomScales.set(domElement, inverseZoomScale);
};
